/**
 * Wave 317 · composer · the only file that knows where blocks go.
 *
 * The composer reads a `Program` + `Dimensions` (derived from specs by
 * `spec-bindings.ts`) and emits a list of `BlockInstance`s plus a default
 * camera framing. The viewer renders the instances; the artist authors
 * the blocks. Three concerns, three modules.
 *
 * The Gênese composition (Day 1 reality):
 *
 *   - Suites along the north edge (long arm of the L). N suites in a row.
 *   - Brise telado wraps the suite façade (one panel per suite).
 *   - Social wing on the south edge (where pool faces).
 *   - Garage on the east tip of the L.
 *   - Pool + deck west of the social wing.
 *   - Garden strip between the L and the lot edge.
 *   - Chimney on the social roof.
 *   - Trees scattered around the perimeter.
 *
 * Coordinate convention (matches the existing HouseScene3D so debugging
 * across versions stays sane):
 *   X →  east (positive)
 *   Y →  up (positive). Always 0 for ground placements; blocks own height.
 *   Z →  south (positive). North is negative Z.
 *
 * Origin (0,0,0) = lot centroid at ground level.
 */

import type {
  ComposedScene,
  BlockInstance,
  Program,
  Dimensions,
} from './types';

export function composeScene(program: Program, dims: Dimensions): ComposedScene {
  const instances: BlockInstance[] = [];
  let key = 0;
  const inst = (
    blockId: BlockInstance['blockId'],
    position: [number, number, number],
    extras: Partial<BlockInstance> = {},
  ): BlockInstance => ({
    blockId,
    position,
    rotation: 0,
    key: `${blockId}-${key++}`,
    ...extras,
  });

  // ─── 1 · Ground + sky ───────────────────────────────────────────
  instances.push(inst('ground', [0, 0, 0]));
  instances.push(inst('sky',    [0, 0, 0]));

  // ─── 2 · Suites along the north edge ────────────────────────────
  // The suite row is centered on the X axis. Each suite is `suiteWM`
  // wide. Z = -shortHalf places them on the north edge of the L.
  const longHalf  = dims.houseLLongM  / 2;
  const shortHalf = dims.houseLShortM / 2;
  const totalSuiteSpan = program.suitesCount * dims.suiteWM;
  const suiteRowStartX = -totalSuiteSpan / 2 + dims.suiteWM / 2;
  const suiteZ = -shortHalf + dims.suiteDM / 2;

  for (let i = 0; i < program.suitesCount; i++) {
    const x = suiteRowStartX + i * dims.suiteWM;
    instances.push(
      inst('suite', [x, 0, suiteZ], {
        paramOverrides: { width: dims.suiteWM, depth: dims.suiteDM },
      }),
    );
    if (program.hasBriseFacade) {
      // Brise sits half a meter in front of the suite façade (south side).
      instances.push(
        inst('brise_panel', [x, 0, suiteZ + dims.suiteDM / 2 + 0.4], {
          paramOverrides: { width: dims.suiteWM },
        }),
      );
    }
  }

  // ─── 3 · Pool + deck west of the social wing ────────────────────
  // The pool centers around X = -longHalf - 4 (so 4m west of the L's
  // west wall) and Z = +shortHalf/3 (south-leaning, between social
  // wing and the south edge).
  if (program.hasPool) {
    instances.push(inst('pool', [-longHalf - 4, 0, shortHalf / 3]));
  }

  // ─── 4 · Garage on the east tip ─────────────────────────────────
  if (program.hasGarage) {
    instances.push(inst('garage', [longHalf + 2, 0, 0]));
  }

  // ─── 5 · Garden strip between L and the lot edge ────────────────
  // One strip on the north side of the suite row, slightly west of center.
  instances.push(inst('garden_strip', [0, 0, -shortHalf - 2.5], {
    paramOverrides: { width: totalSuiteSpan + 4, depth: 1.5 },
  }));

  // ─── 6 · Chimney on the social roof ─────────────────────────────
  if (program.hasChimney) {
    instances.push(inst('chimney', [-longHalf / 3, 0, shortHalf / 4]));
  }

  // ─── 7 · Trees scattered around the perimeter ───────────────────
  // Hand-picked placements (looked-at, not random) so the lot reads as
  // a designed garden, not a forest. 4 trees total.
  const treeSpots: Array<[number, number, number]> = [
    [-longHalf - 7,  0, -shortHalf - 1],   // NW
    [ longHalf + 5,  0, -shortHalf - 0.5], // NE corner
    [-longHalf - 6,  0,  shortHalf + 2],   // SW
    [ longHalf + 4,  0,  shortHalf + 3],   // SE
  ];
  for (const spot of treeSpots) instances.push(inst('tree', spot));

  // ─── 8 · One car parked in the driveway ─────────────────────────
  if (program.hasGarage) {
    instances.push(inst('car', [longHalf + 2, 0, shortHalf + 1.5], { rotation: 0 }));
  }

  // ─── Camera framing (initial look) ──────────────────────────────
  // 3/4 hero shot from south-east, slightly elevated. This is the
  // "first impression" frame. OrbitControls auto-rotates from here.
  const camera: ComposedScene['camera'] = {
    position: [longHalf + 6, 7, shortHalf + 9],
    target: [0, 1.5, 0],
  };

  // ─── Bounds (used by viewer for OrbitControls min/max distance) ─
  const bounds: ComposedScene['bounds'] = {
    min: [-longHalf - 10, 0, -shortHalf - 6],
    max: [longHalf + 8, 6, shortHalf + 6],
  };

  return { instances, camera, bounds };
}
